import { useCallback } from 'react';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { IconName } from '@cbhq/cds-common';
import { ListCell } from '@cbhq/cds-web/cells';
import { Icon } from '@cbhq/cds-web/icons';
import { Box } from '@cbhq/cds-web/layout/Box';
import { Tooltip } from '@cbhq/cds-web/overlays';
import { CoinbaseProfileMenuResource } from '@cbhq/identity-api-hooks/profile-menu/types';

const messages = defineMessages({
  legalEntity: {
    id: 'ResourceItem.legalEntity',
    defaultMessage: 'Entity',
    description: 'Description for the legal entity resource type',
  },
  developer: {
    id: 'ResourceItem.developer',
    defaultMessage: 'Developer',
    description: 'Description for a CDP non-KYB legal entity resource type',
  },
  trust: {
    id: 'ResourceItem.trust',
    defaultMessage: 'Trust',
    description: 'Description for a trust legal entity resource type',
  },
  user: {
    id: 'ResourceItem.user',
    defaultMessage: 'Personal',
    description: 'Description for the personal resource type',
  },
});

const OUTER_SPACING = { paddingX: 0, paddingY: 0 } as const;
const INNER_SPACING = { paddingX: 5, paddingY: 1 } as const;

// The entity "business mark" backgrounds — CDS `--orange5`/`--purple5`/`--green5`,
// matching Workspace Team product icons. Theme-aware (pale in light, dark in
// dark), so applied via the CSS variable in an inline style.
const ENTITY_BUSINESS_MARK_STYLE = { backgroundColor: 'rgb(var(--orange5))' } as const;
const ENTITY_DEVELOPER_MARK_STYLE = { backgroundColor: 'rgb(var(--purple5))' } as const;
const ENTITY_TRUST_MARK_STYLE = { backgroundColor: 'rgb(var(--green5))' } as const;

type EntityVisual = {
  icon: IconName;
  style: { backgroundColor: string };
  message: MessageDescriptor;
};

const DEFAULT_ENTITY_VISUAL: EntityVisual = {
  icon: 'briefcase',
  style: ENTITY_BUSINESS_MARK_STYLE,
  message: messages.legalEntity,
};

// Keyed by the raw Organizations Service EntityType enum name (see
// CoinbaseProfileMenuResource.entityType). Anything not listed here — including
// "ENTITY_TYPE_UNSPECIFIED" and entity types not yet called out for a distinct
// look — falls back to DEFAULT_ENTITY_VISUAL.
const ENTITY_TYPE_VISUALS: Partial<Record<string, EntityVisual>> = {
  ENTITY_TYPE_CDP_NON_KYB: {
    icon: 'api',
    style: ENTITY_DEVELOPER_MARK_STYLE,
    message: messages.developer,
  },
  ENTITY_TYPE_TRUST: {
    icon: 'politicsBuilding',
    style: ENTITY_TRUST_MARK_STYLE,
    message: messages.trust,
  },
};

function getEntityVisual(entityType: string | undefined): EntityVisual {
  return (entityType && ENTITY_TYPE_VISUALS[entityType]) || DEFAULT_ENTITY_VISUAL;
}

type ResourceItemProps = {
  resource: CoinbaseProfileMenuResource;
  onPress: (resourceId: string) => void;
  selected?: boolean;
  /** Show the leading entity-type icon box (redesigned switcher variant). */
  showLeadingIcon?: boolean;
};
export function ResourceItem({ resource, onPress, selected, showLeadingIcon }: ResourceItemProps) {
  const { formatMessage } = useIntl();

  const { name, identityAccountId, resourceType, entityType } = resource;
  if (!identityAccountId) {
    // should never happen
    throw new Error('ResourceItem: identityAccountId is required');
  }

  const entityVisual = getEntityVisual(entityType);

  const description =
    resourceType === 'legal_entity'
      ? formatMessage(entityVisual.message)
      : formatMessage(messages.user);

  const handlePress = useCallback(() => {
    onPress(identityAccountId);
  }, [onPress, identityAccountId]);

  return (
    <Tooltip content={name} placement="right">
      <Box width="100%">
        <ListCell
          compact
          key={identityAccountId}
          title={name}
          description={description}
          media={
            showLeadingIcon ? (
              <Box
                testID="resource-item-leading-icon"
                width="32px"
                height="32px"
                borderRadius={200}
                alignItems="center"
                justifyContent="center"
                style={entityVisual.style}
              >
                <Icon name={entityVisual.icon} size="s" color="fg" />
              </Box>
            ) : undefined
          }
          onClick={handlePress}
          selected={selected}
          borderRadius={0}
          outerSpacing={OUTER_SPACING}
          innerSpacing={INNER_SPACING}
        />
      </Box>
    </Tooltip>
  );
}
